import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/components/ui/use-toast';
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Globe,
  User,
  Briefcase,
  Save,
  Edit,
  ChevronRight,
  Calendar,
  FileText,
  Users,
  TrendingUp,
  Shield,
  Award,
  Clock,
  CheckCircle,
  XCircle,
  PlusCircle,
  Download,
  Printer,
  Info
} from 'lucide-react';

// Industry sectors from VV-DOC-006
const INDUSTRY_SECTORS = [
  'Agro-processing',
  'Food and Beverage Manufacturing',
  'Energy / Utilities',
  'Logistics and Supply Chain',
  'Cement and Construction',
  'Pharmaceutical Manufacturing',
  'Textile and Apparel',
  'Chemical Processing',
  'Cold Chain / Refrigeration',
  'Mining and Quarrying',
  'Automotive Parts',
  'Packaging Manufacturing',
  'Tea Processing',
  'Coffee Processing',
  'Floriculture',
  'Other'
];

// Company size options
const COMPANY_SIZE = [
  '1-50 (Micro enterprise)',
  '51-250 (Small enterprise)',
  '251-1,000 (Medium enterprise)',
  '1,001-5,000 (Large enterprise)',
  'More than 5,000 (Corporate)'
];

// Revenue ranges
const REVENUE_RANGES = [
  'Below KES 10M',
  'KES 10M - 50M',
  'KES 50M - 250M',
  'KES 250M - 1B',
  'Above KES 1B',
  'Prefer not to disclose'
];

// Certification options
const CERTIFICATION_OPTIONS = [
  'ISO 9001 (Quality Management)',
  'ISO 14001 (Environmental Management)',
  'ISO 50001 (Energy Management)',
  'ISO 22000 (Food Safety Management)',
  'HACCP Certification',
  'Kenya Bureau of Standards certification',
  'Global GAP (for agro-processors)',
  'Fair Trade / Rainforest Alliance',
  'ESG reporting framework in use'
];

export default function CompanyProfile() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('company');

  // Comprehensive company profile data matching VV-DOC-006 Section 1
  const [formData, setFormData] = useState({
    // Company Information
    companyName: 'Kenya Breweries Ltd',
    tradingName: 'Kenya Breweries',
    registrationNumber: 'KBL/1990/4567',
    dateOfIncorporation: '1990-06-15',
    registeredAddress: 'Enterprise Road, Industrial Area, Nairobi',
    postalAddress: 'P.O. Box 12345-00100 Nairobi',
    county: 'Nairobi',
    website: 'https://kenyabreweries.com',
    linkedin: 'https://linkedin.com/company/kenya-breweries',
    industrySectors: ['Food and Beverage Manufacturing', 'Agro-processing'],
    otherIndustry: '',
    companySize: 'More than 5,000 (Corporate)',
    annualRevenue: 'Above KES 1B',
    numberOfSites: '5',
    geographicSpread: 'Nairobi, Mombasa, Kisumu, Nakuru, Eldoret',
    yearEstablished: '1990',
    description: 'Leading beverage manufacturer in East Africa, producing beers, ciders, and non-alcoholic beverages. Operating for over 35 years with a strong market presence across the region.',
    
    // Contact Information
    contactName: 'Jane Wanjiku',
    contactTitle: 'Plant Manager',
    contactEmail: 'jane@kenyabreweries.com',
    contactPhone: '+254 722 123 456',
    contactDepartment: 'Operations',
    yearsInRole: '5',
    decisionMaker: 'I report to the decision-maker',
    
    // Secondary Contact
    secondaryName: 'John Omondi',
    secondaryTitle: 'Operations Director',
    secondaryEmail: 'jomondi@kenyabreweries.com',
    secondaryPhone: '+254 733 456 789',
    
    // Certifications
    certifications: ['ISO 9001', 'ISO 14001', 'ISO 22000', 'HACCP Certification'],
    
    // Facility Information
    facilityAge: '35',
    facilitySize: '150,000',
    operatingHours: '24/7',
    operatingDays: '350',
    monitoringSystems: ['SCADA', 'ERP', 'IoT sensors'],
    connectivity: 'Reliable fibre broadband',
    
    // Preferences
    preferredCommunication: 'Email',
    commercialModel: 'Open to discussion',
    budgetRange: 'KES 5M - 10M'
  });

  const handleSave = () => {
    setIsEditing(false);
    toast({
      title: 'Profile Updated',
      description: 'Your company profile has been saved successfully.',
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full max-w-full">
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Breadcrumb navigation */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Button variant="ghost" onClick={() => navigate('/company/dashboard')} className="h-auto p-0 text-muted-foreground hover:text-foreground">
            Dashboard
          </Button>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground font-medium">Company Profile</span>
        </div>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Company Profile</h1>
            <p className="text-muted-foreground mt-1">
              Manage your company information and contact details
            </p>
          </div>
          <Button 
            onClick={() => isEditing ? handleSave() : setIsEditing(true)}
            className={isEditing ? "bg-green-600 hover:bg-green-700" : "bg-blue-600 hover:bg-blue-700"}
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            ) : (
              <>
                <Edit className="h-4 w-4 mr-2" />
                Edit Profile
              </>
            )}
          </Button>
        </div>

        {/* Company Header Card */}
        <Card className="overflow-hidden border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white text-3xl font-bold">
                {getInitials(formData.companyName)}
              </div>
              <div className="flex-1">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <h2 className="text-2xl font-bold">{formData.companyName}</h2>
                    {formData.tradingName && formData.tradingName !== formData.companyName && (
                      <p className="text-muted-foreground">Trading as: {formData.tradingName}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        Reg: {formData.registrationNumber}
                      </Badge>
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Est. {formData.yearEstablished}
                      </Badge>
                      <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                        {formData.companySize}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => window.print()}>
                    <Printer className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
                <p className="mt-4 text-muted-foreground">{formData.description}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="company">Company Details</TabsTrigger>
            <TabsTrigger value="contact">Contact Information</TabsTrigger>
            <TabsTrigger value="facility">Facility & Operations</TabsTrigger>
            <TabsTrigger value="certifications">Certifications</TabsTrigger>
            <TabsTrigger value="preferences">Preferences</TabsTrigger>
          </TabsList>

          {/* Company Details Tab */}
          <TabsContent value="company" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-blue-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">Company Information</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Full Legal Company Name</Label>
                    <Input
                      value={formData.companyName}
                      onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Trading Name</Label>
                    <Input
                      value={formData.tradingName}
                      onChange={(e) => setFormData({...formData, tradingName: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Registration Number</Label>
                    <Input
                      value={formData.registrationNumber}
                      onChange={(e) => setFormData({...formData, registrationNumber: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Date of Incorporation</Label>
                    <Input
                      type="date"
                      value={formData.dateOfIncorporation}
                      onChange={(e) => setFormData({...formData, dateOfIncorporation: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label className="text-sm font-medium">Registered Office Address</Label>
                  <Textarea
                    value={formData.registeredAddress}
                    onChange={(e) => setFormData({...formData, registeredAddress: e.target.value})}
                    disabled={!isEditing}
                    className={!isEditing ? "bg-muted" : ""}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Postal Address</Label>
                    <Input
                      value={formData.postalAddress}
                      onChange={(e) => setFormData({...formData, postalAddress: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">County / Region</Label>
                    <Input
                      value={formData.county}
                      onChange={(e) => setFormData({...formData, county: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Website</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? "bg-muted" : ""}`}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">LinkedIn Profile</Label>
                    <Input
                      value={formData.linkedin}
                      onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="space-y-4">
                  <Label className="text-sm font-medium">Industry Sectors</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {INDUSTRY_SECTORS.map((sector) => (
                      <div key={sector} className="flex items-center space-x-2">
                        <Checkbox
                          id={`sector-${sector}`}
                          checked={formData.industrySectors.includes(sector)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                industrySectors: [...formData.industrySectors, sector]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                industrySectors: formData.industrySectors.filter(s => s !== sector)
                              });
                            }
                          }}
                          disabled={!isEditing}
                        />
                        <Label htmlFor={`sector-${sector}`} className="text-sm">{sector}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Company Size</Label>
                    <Select
                      value={formData.companySize}
                      onValueChange={(value) => setFormData({...formData, companySize: value})}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className={!isEditing ? "bg-muted" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPANY_SIZE.map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Annual Revenue</Label>
                    <Select
                      value={formData.annualRevenue}
                      onValueChange={(value) => setFormData({...formData, annualRevenue: value})}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className={!isEditing ? "bg-muted" : ""}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {REVENUE_RANGES.map((range) => (
                          <SelectItem key={range} value={range}>{range}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Number of Production Sites</Label>
                    <Input
                      type="number"
                      value={formData.numberOfSites}
                      onChange={(e) => setFormData({...formData, numberOfSites: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Geographic Spread</Label>
                    <Input
                      value={formData.geographicSpread}
                      onChange={(e) => setFormData({...formData, geographicSpread: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Information Tab */}
          <TabsContent value="contact" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-blue-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <User className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">Contact Information</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <h3 className="text-md font-semibold mb-4 text-blue-700">Primary Contact</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Full Name</Label>
                    <Input
                      value={formData.contactName}
                      onChange={(e) => setFormData({...formData, contactName: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Job Title</Label>
                    <Input
                      value={formData.contactTitle}
                      onChange={(e) => setFormData({...formData, contactTitle: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={formData.contactEmail}
                        onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? "bg-muted" : ""}`}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Phone Number</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={formData.contactPhone}
                        onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? "bg-muted" : ""}`}
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Department</Label>
                    <Input
                      value={formData.contactDepartment}
                      onChange={(e) => setFormData({...formData, contactDepartment: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Years in Role</Label>
                    <Input
                      type="number"
                      value={formData.yearsInRole}
                      onChange={(e) => setFormData({...formData, yearsInRole: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <Separator className="my-6" />

                <h3 className="text-md font-semibold mb-4 text-blue-700">Secondary Contact</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Contact Name</Label>
                    <Input
                      value={formData.secondaryName}
                      onChange={(e) => setFormData({...formData, secondaryName: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Contact Title</Label>
                    <Input
                      value={formData.secondaryTitle}
                      onChange={(e) => setFormData({...formData, secondaryTitle: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="email"
                        value={formData.secondaryEmail}
                        onChange={(e) => setFormData({...formData, secondaryEmail: e.target.value})}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? "bg-muted" : ""}`}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Secondary Phone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        value={formData.secondaryPhone}
                        onChange={(e) => setFormData({...formData, secondaryPhone: e.target.value})}
                        disabled={!isEditing}
                        className={`pl-10 ${!isEditing ? "bg-muted" : ""}`}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Facility & Operations Tab */}
          <TabsContent value="facility" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-blue-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">Facility & Operations</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Facility Age (years)</Label>
                    <Input
                      type="number"
                      value={formData.facilityAge}
                      onChange={(e) => setFormData({...formData, facilityAge: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Facility Size (sq m)</Label>
                    <Input
                      type="number"
                      value={formData.facilitySize}
                      onChange={(e) => setFormData({...formData, facilitySize: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Operating Hours</Label>
                    <Input
                      value={formData.operatingHours}
                      onChange={(e) => setFormData({...formData, operatingHours: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Operating Days per Year</Label>
                    <Input
                      type="number"
                      value={formData.operatingDays}
                      onChange={(e) => setFormData({...formData, operatingDays: e.target.value})}
                      disabled={!isEditing}
                      className={!isEditing ? "bg-muted" : ""}
                    />
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label className="text-sm font-medium">Monitoring Systems</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {['SCADA', 'ERP', 'MES', 'IoT sensors', 'Manual records', 'Energy management'].map((system) => (
                      <div key={system} className="flex items-center space-x-2">
                        <Checkbox
                          id={`system-${system}`}
                          checked={formData.monitoringSystems.includes(system)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({
                                ...formData,
                                monitoringSystems: [...formData.monitoringSystems, system]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                monitoringSystems: formData.monitoringSystems.filter(s => s !== system)
                              });
                            }
                          }}
                          disabled={!isEditing}
                        />
                        <Label htmlFor={`system-${system}`} className="text-sm">{system}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2 mt-4">
                  <Label className="text-sm font-medium">Connectivity</Label>
                  <Select
                    value={formData.connectivity}
                    onValueChange={(value) => setFormData({...formData, connectivity: value})}
                    disabled={!isEditing}
                  >
                    <SelectTrigger className={!isEditing ? "bg-muted" : ""}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Reliable fibre broadband">Reliable fibre broadband</SelectItem>
                      <SelectItem value="Mobile data (4G/3G) only">Mobile data (4G/3G) only</SelectItem>
                      <SelectItem value="Limited / intermittent connectivity">Limited / intermittent connectivity</SelectItem>
                      <SelectItem value="Secure corporate network">Secure corporate network</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Certifications Tab */}
          <TabsContent value="certifications" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-blue-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">Certifications</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {CERTIFICATION_OPTIONS.map((cert) => (
                    <div key={cert} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cert-${cert}`}
                        checked={formData.certifications.includes(cert)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setFormData({
                              ...formData,
                              certifications: [...formData.certifications, cert]
                            });
                          } else {
                            setFormData({
                              ...formData,
                              certifications: formData.certifications.filter(c => c !== cert)
                            });
                          }
                        }}
                        disabled={!isEditing}
                      />
                      <Label htmlFor={`cert-${cert}`} className="text-sm">{cert}</Label>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Preferences Tab */}
          <TabsContent value="preferences" className="space-y-6">
            <Card className="overflow-hidden border-l-4 border-l-blue-500">
              <CardHeader className="bg-muted/30 py-3">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <CardTitle className="text-lg">Preferences</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Preferred Communication Method</Label>
                  <Select
                    value={formData.preferredCommunication}
                    onValueChange={(value) => setFormData({...formData, preferredCommunication: value})}
                    disabled={!isEditing}
                  >
                    <SelectTrigger className={!isEditing ? "bg-muted" : ""}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Email">Email</SelectItem>
                      <SelectItem value="Phone call">Phone call</SelectItem>
                      <SelectItem value="Video call">Video call</SelectItem>
                      <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 mt-4">
                  <Label className="text-sm font-medium">Preferred Commercial Model</Label>
                  <Select
                    value={formData.commercialModel}
                    onValueChange={(value) => setFormData({...formData, commercialModel: value})}
                    disabled={!isEditing}
                  >
                    <SelectTrigger className={!isEditing ? "bg-muted" : ""}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="One-time purchase">One-time purchase</SelectItem>
                      <SelectItem value="Annual subscription">Annual subscription</SelectItem>
                      <SelectItem value="Pay-from-savings">Pay-from-savings</SelectItem>
                      <SelectItem value="Open to discussion">Open to discussion</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 mt-4">
                  <Label className="text-sm font-medium">Budget Range (KES/year)</Label>
                  <Select
                    value={formData.budgetRange}
                    onValueChange={(value) => setFormData({...formData, budgetRange: value})}
                    disabled={!isEditing}
                  >
                    <SelectTrigger className={!isEditing ? "bg-muted" : ""}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Below KES 1M">Below KES 1M</SelectItem>
                      <SelectItem value="KES 1M - 5M">KES 1M - 5M</SelectItem>
                      <SelectItem value="KES 5M - 10M">KES 5M - 10M</SelectItem>
                      <SelectItem value="KES 10M - 25M">KES 10M - 25M</SelectItem>
                      <SelectItem value="Above KES 25M">Above KES 25M</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Stats Card */}
        <Card className="overflow-hidden border-l-4 border-l-blue-500 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Profile Completeness</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Your profile information will be automatically used when submitting problems to the Problem Bank.
                  Keeping it updated helps speed up the review process.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}